// Compatibility shim for Rebuy Smart Cart mode.
//
// When `settings.cart_drawer_provider == 'rebuy'`, the Dawn `<cart-drawer>` element is no
// longer rendered (theme.liquid) and `assets/cart-drawer.js` is not loaded (header.liquid).
// But five callers still do `document.querySelector('cart-drawer').renderContents(...)` after
// an add — frequently-bought.js (postBundleToCart), free-gift-modal.js, progress-bar.js,
// free-gift-validator.js, rebuy-bundle-validator.js. With the real element gone those would
// throw on a null lookup.
//
// This shim defines a hidden `<cart-drawer>` element that resolves the lookup and maps the
// Dawn drawer API onto Rebuy Smart Cart: renderContents() opens Smart Cart (and asks Rebuy to
// refresh so a freshly-added line shows). It degrades to a no-op if `window.Rebuy` is not ready,
// so a failed/late Rebuy load never breaks the add flow.

import { syncCartUI, updateCartBubble } from './rebuy-cart-sync.js';

// Force Rebuy to re-read the Shopify cart. An item added via the Shopify AJAX API (product-form,
// FBT, etc.) is NOT added through Rebuy.Cart, so Smart Cart shows a stale cart until it re-syncs.
// The real method is `Rebuy.Cart.fetchCart()` (there is no `.refresh()` — calling it silently
// no-ops and the drawer opens empty, then the item pops in ~400ms later).
const refreshRebuyCart = (rebuy) => {
    try {
        if (rebuy.Cart && typeof rebuy.Cart.fetchCart === 'function') {
            rebuy.Cart.fetchCart();
        }
    } catch (error) {
        console.error('Rebuy cart fetch failed:', error);
    }
};

// Wait until the Smart Cart flyout DOM is actually populated before we show it, so it opens with the
// item visible — not empty-then-pop. Rebuy's `.rebuy-cart` carries a `no-items` class while empty;
// it clears (~500ms after an add) once Rebuy re-renders the flyout, and crucially it clears WHILE
// the drawer is still hidden (because we set skip_open). Waiting on the count alone is not enough:
// Rebuy's cart cache reports the new count before the flyout DOM re-renders, so the drawer would
// still flash empty. The `no-items` class is the true "rendered with items" signal.
// `waitForPopulated=false` (plain cart-icon open) resolves immediately. Always resolves by timeout.
//
// Timeout is the give-up only — on a normal connection `no-items` clears in ~500ms and we resolve
// then, so raising it is free there. It's sized for slow networks (3G): Rebuy's flyout re-render can
// take several seconds, and a shorter give-up was opening the drawer EMPTY before the item rendered.
// Trade-off: if Rebuy genuinely never populates, the open is delayed by this much before showing empty.
const waitForDrawerPopulated = (waitForPopulated, timeout = 6000) => {
    if (!waitForPopulated) return Promise.resolve();
    return new Promise((resolve) => {
        const start = Date.now();
        const tick = () => {
            const root = document.querySelector('.rebuy-cart');
            if (root && !root.classList.contains('no-items')) {
                resolve();
            } else if (Date.now() - start > timeout) {
                resolve();
            } else {
                setTimeout(tick, 80);
            }
        };
        tick();
    });
};

// True once the lazy Smart Cart module is bound. Also suppresses Rebuy's auto-open-on-add the moment
// the module exists — so even when the module is first loaded on-demand by an add (not preloaded),
// `skip_open` lands as soon as it can and the drawer never flashes empty.
const smartCartReady = (rebuy) => {
    if (rebuy.SmartCart && typeof rebuy.SmartCart.show === 'function') {
        rebuy.SmartCart.skip_open = true;
        return true;
    }
    return false;
};

const ensureSmartCartReady = (rebuy) => {
    if (smartCartReady(rebuy)) {
        return Promise.resolve(true);
    }
    // On-demand import (lazy): we do NOT preload Smart Cart on every page load — it's imported here,
    // only when a click or add actually needs it, to keep it off the main thread otherwise.
    if (typeof rebuy.importSmartCartModule === 'function') {
        try {
            rebuy.importSmartCartModule();
        } catch (error) {
            console.error('Rebuy importSmartCartModule failed:', error);
        }
    }
    return new Promise((resolve) => {
        const start = Date.now();
        const timer = setInterval(() => {
            if (smartCartReady(rebuy)) {
                clearInterval(timer);
                resolve(true);
            } else if (Date.now() - start > 5000) {
                clearInterval(timer);
                resolve(false);
            }
        }, 150);
    });
};

// Open the Smart Cart, importing the lazy module first if needed. `afterAdd` (an add just happened)
// makes us refresh Rebuy's cart and wait until the flyout DOM is populated BEFORE showing, so the
// drawer opens already showing the item instead of flashing empty. Returns true on success / false
// if Rebuy never becomes ready (caller just logs; the icon has no /cart fallback by product decision).
//
// rebuy.js is hardcoded async, so for the first beat after load `window.Rebuy` may not exist yet.
// Poll briefly for it so an early cart-icon click opens the drawer a moment later rather than being
// dropped. Resolves the Rebuy global once present, or null after a timeout (failed/blocked load).
const waitForRebuyGlobal = (timeout = 10000) => {
    if (window.Rebuy) return Promise.resolve(window.Rebuy);
    return new Promise((resolve) => {
        const start = Date.now();
        const timer = setInterval(() => {
            if (window.Rebuy) {
                clearInterval(timer);
                resolve(window.Rebuy);
            } else if (Date.now() - start > timeout) {
                clearInterval(timer);
                resolve(null);
            }
        }, 100);
    });
};

const openSmartCart = async (afterAdd) => {
    const rebuy = await waitForRebuyGlobal();
    if (!rebuy) {
        console.warn('Rebuy not ready; cannot open Smart Cart.');
        return false;
    }
    const ready = await ensureSmartCartReady(rebuy);
    if (!ready) {
        console.warn('Rebuy Smart Cart module unavailable; skipping drawer open.');
        return false;
    }
    refreshRebuyCart(rebuy);
    await waitForDrawerPopulated(afterAdd);
    try {
        rebuy.SmartCart.show();
        return true;
    } catch (error) {
        console.error('Rebuy Smart Cart show failed:', error);
        return false;
    }
};

class CartDrawerShim extends HTMLElement {
    // Dawn API surface used by the five callers. `parsedState` is the /cart/add response, which
    // carries the new `item_count`. We don't render the drawer markup — Smart Cart owns that — but
    // we update the header bubble instantly from that count, then let Rebuy's cart.change event
    // (which also fires for native adds) reconcile everything else via rebuy-cart-sync.
    renderContents(parsedState) {
        if (parsedState && typeof parsedState.item_count === 'number') {
            updateCartBubble(parsedState.item_count);
        }
        syncCartUI();
        // Open AFTER the flyout is populated so the drawer never flashes empty (afterAdd = true).
        openSmartCart(true);
    }

    // Callers build a `sections=` POST param from this list; keep the same ids so the request
    // shape is unchanged even though the returned section HTML is ignored in Smart Cart mode.
    getSectionsToRender() {
        return [{ id: 'cart-drawer' }, { id: 'cart-icon-bubble' }];
    }

    // product-form.js / quick-add.js call `this.cart.setActiveElement(document.activeElement)`
    // BEFORE posting to /cart/add (mirrors Dawn's cart-drawer.js). Without it the add throws and
    // nothing is added to the cart. Store it for parity; we don't use it (Rebuy owns focus).
    setActiveElement(element) {
        this.activeElement = element;
    }
}

// Bind the header cart icon to Smart Cart. The icon is a plain `<a href="/cart">`; in Dawn mode
// cart-drawer.js intercepted the click, but that script is not loaded in rebuy mode. In rebuy mode
// the icon must ONLY ever open the Smart Cart drawer — it must NEVER navigate to the /cart page
// (per product decision). So we always preventDefault, and never fall back to the href.
//
// No readiness gate / loading spinner: rebuy.js is now hardcoded + async in theme.liquid so it loads
// early. `openSmartCart()` self-heals — if the Smart Cart module isn't imported yet it imports and
// waits (up to 5s) before showing — so an early click opens the drawer rather than being lost.
const bindCartIcon = () => {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.smartCartBound) return;
    cartLink.dataset.smartCartBound = 'true';
    cartLink.addEventListener('click', async (event) => {
        // The icon never navigates to /cart in rebuy mode — it only opens the drawer.
        event.preventDefault();
        await openSmartCart();
    });
    // The icon is <a role="button"> with no href, so the browser doesn't map Enter/Space to a click.
    // Wire them up so the cart is keyboard-operable (a11y).
    cartLink.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            cartLink.click();
        }
    });
};

// Suppress Rebuy's auto-open-on-add. By default Rebuy opens Smart Cart the instant it detects a
// cart add — BEFORE its cart copy syncs (~400ms) — so the drawer flashes EMPTY then the item pops
// in. `Rebuy.SmartCart.skip_open = true` disables that (manual show() still works); we then open the
// drawer ourselves, already populated, from renderContents/the cart icon. The flag is set by
// `smartCartReady` whenever the module is detected — by this observe poll, or on-demand from an add.
let readinessTimer = null;
const waitForSmartCartReady = () => {
    // Re-runnable: a prior poll (e.g. from a bfcache restore) is cleared first.
    clearInterval(readinessTimer);
    const start = Date.now();
    // Observe-only: we do NOT eagerly import the Smart Cart module here (that cost main-thread time
    // on every pageview, even cart-less ones — see Synchro's 0ms TBT). We just watch for Rebuy to
    // load it naturally; `smartCartReady` sets `skip_open` the moment it appears. If a cart add
    // happens first, ensureSmartCartReady imports it on-demand and sets `skip_open` there instead.
    readinessTimer = setInterval(() => {
        const rebuy = window.Rebuy;
        if (rebuy && smartCartReady(rebuy)) {
            clearInterval(readinessTimer);
            return;
        }
        // Give it 15s, then stop polling. A cart click/add still self-heals via ensureSmartCartReady.
        if (Date.now() - start > 15000) clearInterval(readinessTimer);
    }, 150);
};

// Start the observe poll on load so `skip_open` is set as soon as Rebuy loads Smart Cart (see above).
const disableRebuyAutoOpen = () => waitForSmartCartReady();

// Browser Back restores the page from bfcache, but Rebuy's Smart Cart module isn't re-bound yet.
// Re-run the readiness poll so `skip_open` is re-set once Rebuy re-binds (a click meanwhile still
// self-heals via openSmartCart).
const reinitOnBfcacheRestore = () => {
    window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        waitForSmartCartReady();
    });
};

// Wire the shim. Safe to call once on load in rebuy mode only (see 10x_global.js gate).
// The `<cart-drawer>` element is server-rendered in theme.liquid (so product-form/quick-add capture
// it synchronously at construct time); here we just upgrade it via customElements.define.
export const initCartDrawerShim = () => {
    if (!customElements.get('cart-drawer')) {
        customElements.define('cart-drawer', CartDrawerShim);
    }
    bindCartIcon();
    disableRebuyAutoOpen();
    reinitOnBfcacheRestore();
};

export default initCartDrawerShim;
